import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFirebase } from "../firebase";
import {
  HlsAudioActionsContext,
  HlsAudioStateContext,
} from "./HlsAudioContext";

/**
 * Provide HLS audio playback capabilities to descendants.
 * Wrap your app with this provider and use the `useHlsAudio` hook.
 *
 * @param {{ children: import('react').ReactNode }} props React children
 * @returns {import('react').ReactElement}
 */
export const HlsAudioWrapper = ({ children }) => {
  const audioRef_hls = useRef();
  const audioRef_preview = useRef(new Audio());
  const hls_ref = useRef();
  const { auth, currentUser } = useFirebase();
  const [token, setToken] = useState(null);
  const [hlsTrackState, setHlsTrackState] = useState({
    isPlaying: false,
    isLoading: false,
    currentTime: 0,
    duration: 0,
    isMuted: true,
  });
  const { fetchSecureFile, getStorageUrl } = useFirebase();
  const [currentTrackData, setCurrentTrackData] = useState();
  const currentTrackDataRef = useRef();
  const [isInRoom, setIsInRoom] = useState(false);
  const [previewTrack, setPreviewTrack] = useState();
  const previewAbortController = useRef(null);
  // Track time updates for progress
  // Add this at the top of the HlsAudioWrapper component
  const lastUpdateTimeRef = useRef(0);
  const THROTTLE_INTERVAL = 1000; // Update every 100ms

  const [Hls, setHLS] = useState();

  useEffect(() => {
    function handleClick() {
      console.log("Unmuting audio...");
      setHlsTrackState((prev) => ({ ...prev, isMuted: false }));
      audioRef_hls.current.muted = false;
      document.removeEventListener("click", handleClick, true);
    }

    if (audioRef_hls.current) {
      document.addEventListener("click", handleClick, true);
    }
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [audioRef_hls.current]);

  // Hook to audioRef_preview and fire when preview audio ends
  useEffect(() => {
    const audio = audioRef_preview.current;
    if (!audio) return;

    audio.addEventListener("ended", stopTrackPreview);

    return () => {
      audio.removeEventListener("ended", stopTrackPreview);
    };
  }, []);

  useEffect(() => {
    async function init() {
      if (!currentUser) return;
      await loadHls();
      if (!audioRef_hls.current) {
        const el = new Audio();
        el.muted = true;
        audioRef_hls.current = el;
      }
      if (!Hls) return;
      prepareHLS();
    }
    init();
    return () => {
      hls_ref.current?.destroy();
    };
  }, [currentUser, Hls]);

  async function loadHls() {
    const _hls = (await import("hls.js")).default;
    setHLS(() => _hls);
  }

  /**
   * Prepare the HLS instance and capture the auth token.
   * Must be called before attempting to play any track.
   * @returns {Promise<void>}
   */
  async function prepareHLS() {
    if (!currentUser) {
      // throw new Error("User not authenticated");
      return;
    }

    // Ensure audio element is available
    if (!audioRef_hls.current) {
      console.log("Audio element not ready yet, waiting...");
      return;
    }

    const idToken = await currentUser.getIdToken();

    hls_ref.current = new Hls({
      xhrSetup: (xhr) => {
        xhr.setRequestHeader("Authorization", "Bearer " + idToken);
      },
    });

    setToken(idToken);
  }
  const playTrack = useCallback(
    async (post, isInRoom) => {
      if (!audioRef_hls.current) {
        console.error("Audio element not ready");
        return;
      }

      reset();
      setIsInRoom(isInRoom);
      setCurrentTrackData(post);
      currentTrackDataRef.current = post;
      setHlsTrackState((prev) => ({ ...prev, isLoading: true }));
      const trackPath = `tracks/${post?.user?.id}/${post?.id}/compressed/`;
      const playlistPath = `${trackPath}hls-stream.m3u8?alt=media`;
      const res = await fetchSecureFile({ path: playlistPath });
      let playlist = await res.text();

      const baseUrl = getStorageUrl();

      console.log(`${baseUrl}${encodeURIComponent(trackPath)}`);
      // return;

      // Rewrite all .ts entries to absolute URLs
      playlist = playlist.replace(/(.+\.ts)/g, (match) => {
        return `${baseUrl}${encodeURIComponent(trackPath)}${match}?alt=media`;
      });

      const blob = new Blob([playlist], {
        type: "application/vnd.apple.mpegurl",
      });
      const blobUrl = URL.createObjectURL(blob);

      hls_ref.current?.loadSource(blobUrl);
      hls_ref.current?.attachMedia(audioRef_hls.current);
      audioRef_hls.current.playsInline = true;

      hls_ref.current.on(Hls.Events.FRAG_LOADING, (event, data) => {
        const frag = data.frag;
        const currentTime = audioRef_hls.current.currentTime;
        const isCurrentTimeInFragment =
          currentTime >= frag.start &&
          currentTime <= frag.start + frag.duration;

        isCurrentTimeInFragment &&
          setHlsTrackState((prev) => ({ ...prev, isLoading: true }));
      });
      hls_ref.current.on(Hls.Events.FRAG_LOADED, (event, data) => {
        const frag = data.frag;
        const currentTime = audioRef_hls.current.currentTime;
        const isCurrentTimeInFragment =
          currentTime >= frag.start &&
          currentTime <= frag.start + frag.duration;

        isCurrentTimeInFragment &&
          setHlsTrackState((prev) => ({ ...prev, isLoading: false }));
      });

      function onBuffered() {
        const playPromise = audioRef_hls.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setHlsTrackState((prev) => ({
                ...prev,
                isPlaying: true,
                isLoading: false,
              }));
            })
            .catch((error) => {
              setHlsTrackState((prev) => ({
                ...prev,
                isPlaying: false,
                isLoading: false,
              }));
              console.error("Play failed:", error);
              console.log("Error name:", error.name);
            });
        }
      }

      hls_ref.current.on(Hls.Events.FRAG_BUFFERED, onBuffered);

      // Unsubscribe from FRAG_BUFFERED when audio is paused
      audioRef_hls.current.addEventListener("pause", () => {
        try {
          hls_ref.current?.off(Hls.Events.FRAG_BUFFERED, onBuffered);
        } catch (_) {}
      });

      // Re-subscribe if audio resumes playing
      audioRef_hls.current.addEventListener("play", () => {
        try {
          hls_ref.current?.on(Hls.Events.FRAG_BUFFERED, onBuffered);
        } catch (_) {}
      });

      // In the timeupdate event listener, replace the current setHlsTrackState with:
      audioRef_hls.current.addEventListener("timeupdate", () => {
        if (currentTrackDataRef.current) {
        }

        // Throttle the state update
        const now = Date.now();
        if (now - lastUpdateTimeRef.current >= THROTTLE_INTERVAL) {
          setHlsTrackState((prev) => ({
            ...prev,
            currentTime: audioRef_hls.current.currentTime,
            duration: isNaN(audioRef_hls.current.duration)
              ? null
              : audioRef_hls.current.duration,
          }));
          lastUpdateTimeRef.current = now;
        }
      });
    },
    [Hls]
  );

  const playTrackPreview = useCallback(
    async (userId, trackId, currentTrackData) => {
      // Cancel any previous preview
      if (previewAbortController.current) {
        previewAbortController.current.abort();
      }

      previewAbortController.current = new AbortController();
      setPreviewTrack({ data: currentTrackData, isLoading: true });

      try {
        const res = await fetchSecureFile({
          path: `tracks/${userId}/${trackId}/engaging-part/faded.mp3?alt=media`,
          signal: previewAbortController.current.signal,
        });

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        audioRef_preview.current.src = url;
        audioRef_preview.current.play();

        setPreviewTrack((prev) => ({
          ...prev,
          isLoading: false,
          isPlaying: true,
        }));
      } catch (error) {
        if (error.name === "AbortError") {
          // Preview was cancelled, do nothing
          return;
        }
        console.log("Aborted...");

        throw error;
      }
    },
    [Hls]
  );

  const stopTrackPreview = useCallback(async () => {
    if (previewAbortController.current) {
      previewAbortController.current.abort();
      previewAbortController.current = null;
    }
    audioRef_preview.current.pause();
    setPreviewTrack(null);
  }, [Hls]);

  /**
   * Pause the current playback.
   * @returns {void}
   */
  const pauseTrack = useCallback(() => {
    audioRef_hls.current?.pause();
    setHlsTrackState((prev) => ({ ...prev, isPlaying: false }));
  }, [Hls]);

  /**
   * Seek to a given point in the current track.
   * @param {number} toSeconds Target time in seconds
   * @returns {void}
   */
  const seekTrack = useCallback(
    (toSeconds) => {
      console.log("Called seektrack");
      if (
        typeof toSeconds === "number" &&
        toSeconds <= hlsTrackState.duration
      ) {
        audioRef_hls.current.currentTime = toSeconds;
        setHlsTrackState((prev) => ({ ...prev, currentTime: toSeconds }));
      }
    },
    [hlsTrackState.duration]
  );

  const resumePlay = useCallback(() => {
    audioRef_hls.current.play();
    setHlsTrackState((prev) => ({
      ...prev,
      isPlaying: true,
    }));
  }, []);

  const reset = useCallback(() => {
    setCurrentTrackData(null);
    currentTrackDataRef.current = null;
    audioRef_hls.current?.pause();
    hls_ref.current?.stopLoad();
  }, []);

  const muteUnmute = useCallback(() => {
    let newMuteState = false;
    setHlsTrackState((prev) => {
      newMuteState = !prev.isMuted;
      return { ...prev, isMuted: !prev.isMuted };
    });
    audioRef_hls.current.muted = newMuteState;
  }, []);

  const actions = useMemo(
    () => ({
      playTrack,
      pauseTrack,
      seekTrack,
      resumePlay,
      reset,
      playTrackPreview,
      stopTrackPreview,
      muteUnmute,
    }),
    [
      playTrack,
      pauseTrack,
      seekTrack,
      resumePlay,
      reset,
      playTrackPreview,
      stopTrackPreview,
      muteUnmute,
    ]
  );

  const value = useMemo(
    () => ({
      hlsTrackState,
      currentTrackData,
      previewTrack,
      isInRoom,
    }),
    [hlsTrackState, currentTrackData, previewTrack, isInRoom]
  );

  const providerValue = useMemo(
    () => ({
      state: value, // this will update every tick
      actions, // this will stay stable
    }),
    [value, actions]
  );

  return (
    <HlsAudioActionsContext.Provider value={actions}>
      <HlsAudioStateContext.Provider value={value}>
        {children}
      </HlsAudioStateContext.Provider>
    </HlsAudioActionsContext.Provider>
  );
};
